import { useState } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { trackEvent } from "@/lib/analytics";
import ComingSoonSheet, {
  type ComingSoonMethod,
} from "./ComingSoonSheet";

export type AddOutfitMethod = "screenshot" | "social" | "url" | "text";

interface AddOutfitSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadScreenshot: () => void;
  onPasteUrl: () => void;
  onOpenText: () => void;
  onOpenSocial: () => void;
}

interface SlotSpec {
  method: AddOutfitMethod;
  icon: string;
  title: string;
  sublabel: string;
}

const SLOTS: SlotSpec[] = [
  {
    method: "screenshot",
    icon: "ti-camera",
    title: "Upload a screenshot",
    sublabel: "A photo of an outfit you've seen",
  },
  {
    method: "social",
    icon: "ti-brand-instagram",
    title: "Add from social media",
    sublabel: "TikTok, Instagram, Pinterest",
  },
  {
    method: "url",
    icon: "ti-link",
    title: "Paste a URL",
    sublabel: "Any retailer or product page",
  },
  {
    method: "text",
    icon: "ti-text-plus",
    title: "Describe an outfit",
    sublabel: "Tell us what you're looking for",
  },
];

export default function AddOutfitSheet({
  open,
  onOpenChange,
  onUploadScreenshot,
  onPasteUrl,
  onOpenText,
  onOpenSocial,
}: AddOutfitSheetProps) {
  const [comingSoon, setComingSoon] = useState<ComingSoonMethod | null>(null);

  const handleSlot = (slot: SlotSpec) => {
    void triggerHaptic("selection");
    trackEvent("add_outfit_method_selected", { method: slot.method });

    if (slot.method === "screenshot") {
      onUploadScreenshot();
      return;
    }

    if (slot.method === "url") {
      onOpenChange(false);
      onPasteUrl();
      return;
    }

    if (slot.method === "text") {
      onOpenChange(false);
      onOpenText();
      return;
    }

    if (slot.method === "social") {
      onOpenChange(false);
      onOpenSocial();
      return;
    }

    onOpenChange(false);
    setComingSoon(slot.method as ComingSoonMethod);
  };

  return (
    <>
      <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DrawerPrimitive.Portal>
          <DrawerPrimitive.Overlay
            className="fixed inset-0 z-[60]"
            style={{ background: "rgba(44,40,32,0.4)" }}
          />
          <DrawerPrimitive.Content
            className="fixed inset-x-0 bottom-0 z-[61] flex flex-col"
            style={{
              background: "#FFFCF8",
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              paddingBottom: "calc(8px + var(--safe-bottom))",
              outline: "none",
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 36,
                height: 4,
                borderRadius: 999,
                background: "#E0D8D0",
                margin: "8px auto",
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "0 16px 4px",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="l2f-type-section-header"
                  style={{ color: "#C84B2F" }}
                >
                  ADD AN OUTFIT
                </div>
                <DrawerPrimitive.Title
                  style={{
                    fontSize: 20,
                    fontStyle: "italic",
                    fontFamily:
                      "var(--font-serif), 'Instrument Serif', Georgia, serif",
                    color: "#2C2820",
                    lineHeight: 1.1,
                    marginTop: 2,
                    fontWeight: 400,
                  }}
                >
                  How would you like to start?
                </DrawerPrimitive.Title>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => onOpenChange(false)}
                style={{
                  width: 44,
                  height: 44,
                  background: "transparent",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                  padding: 0,
                  margin: "-6px -6px 0 0",
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 999,
                    background: "#F1EFE8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="ti ti-x"
                    style={{ fontSize: 14, color: "#4A4540" }}
                  />
                </span>
              </button>
            </div>
            <DrawerPrimitive.Description className="sr-only">
              Choose how to add an outfit.
            </DrawerPrimitive.Description>

            <div style={{ padding: "14px 16px 8px" }}>
              {SLOTS.map((slot, idx) => (
                <button
                  key={slot.method}
                  type="button"
                  onClick={() => handleSlot(slot)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    padding: 14,
                    background: "transparent",
                    border: "none",
                    textAlign: "left",
                    borderBottom:
                      idx === SLOTS.length - 1
                        ? "none"
                        : "0.5px solid #F1EFE8",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 14,
                      background: "#FAECE7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className={`ti ${slot.icon}`}
                      style={{ fontSize: 22, color: "#993C1D" }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: "#2C2820",
                        fontWeight: 500,
                      }}
                    >
                      {slot.title}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#6B6560",
                        marginTop: 1,
                        lineHeight: 1.4,
                      }}
                    >
                      {slot.sublabel}
                    </div>
                  </div>
                  <i
                    className="ti ti-chevron-right"
                    style={{ fontSize: 14, color: "#9B958E", flexShrink: 0 }}
                  />
                </button>
              ))}
            </div>
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Portal>
      </DrawerPrimitive.Root>

      <ComingSoonSheet
        open={comingSoon !== null}
        method={comingSoon}
        onOpenChange={(o) => {
          if (!o) setComingSoon(null);
        }}
      />
    </>
  );
}
